<script setup lang="ts">
import { FESearchResult } from "@/models/frontend-only/FESearchResult";
import { LoopMultitrack } from "@/models/LoopMultitrack";
import { SongSearchResult } from "@/models/SongSearchResult";

const beta = useBetaStore();
const api = useApiStore();
const props = withDefaults(
  defineProps<{
    songs: FESearchResult[];
    showRank?: boolean;
    classes?: string;
    loadingNumber?: number | null;
    dontGoToSong?: boolean;
    reorder?: boolean;
    hideExtraIcons?: boolean;
    showAlbumArtwork?: boolean;
    dataTitle?: string;
    showWeeklyTrends?: boolean;
    largeAlbumArtwork?: boolean;
    showChartData?: boolean;
    showBadges?: boolean;
  }>(),
  {
    showRank: false,
    classes: undefined,
    dataTitle: "",
    loadingNumber: null,
  },
);
const enableSongVersions = beta.features.recordingFocusLayout;

const loopData = ref<LoopMultitrack[] | null>(null);
const wrapper = ref<HTMLDivElement>();
let sortableObj: any = null;
const dragging = ref(false);
watch(() => props.reorder, onReorder);
async function onReorder() {
  if (props.reorder && wrapper.value) {
    const Sortable = (await import("sortablejs")).default;

    sortableObj = new Sortable(wrapper.value, {
      animation: 150,
      onStart: () => (dragging.value = true),
      onEnd: (evt) => {
        const oldIndex = evt.oldIndex;
        const newIndex = evt.newIndex;
        if (typeof oldIndex !== "number" || typeof newIndex !== "number") {
          return;
        }
        const song = songs.value.splice(oldIndex, 1);
        songs.value.splice(newIndex, 0, ...song);
        dragging.value = false;
      },
    });
  } else {
    sortableObj?.destroy();
    sortableObj = null;
  }
}

const songs = computed({
  get: () => props.songs,
  set: (songs: SongSearchResult[]) => emit("update:songs", songs),
});

async function getLoopMultitrackLinks() {
  loopData.value = null;

  if (!props.songs.length) {
    return;
  }

  try {
    const songNumbers = props.songs.map((i) => i.songNumber);
    loopData.value = (await api.post("/GetLoopMultitrackLinks", {
      songNumbers,
    })) as LoopMultitrack[];
  } catch (e) {
    console.error("Error getting loop multitrack links", e);
  }
}
onMounted(() => getLoopMultitrackLinks());

watch(
  () => props.songs,
  () => getLoopMultitrackLinks(),
  { deep: true },
);

function getMultitracksUrl(songNumber: string) {
  return loopData.value?.find((l) => l.songNumber === songNumber)?.url || false;
}

function goToMultitracksLink(songNumber: string) {
  const url = getMultitracksUrl(songNumber);
  if (!url) {
    return;
  }
  window.open(url, "_blank");
}
const emit = defineEmits<{
  (e: "click", songNumber: string, slug: string, songTitle: string): void;
  (e: "update:songs", songs: SongSearchResult[]): void;
}>();
function onClick(
  e: Event,
  songNumber: string,
  slug: string,
  songTitle: string,
) {
  emit("click", songNumber, slug, songTitle);
  if (props.dontGoToSong) {
    e.stopPropagation();
    e.preventDefault();
    return false;
  }
}

const componentIs = computed(() => {
  if (props.reorder) {
    return "div";
  }
  if (props.dontGoToSong) {
    return "button";
  }
  return "router-link";
});
</script>

<template>
  <div
    v-if="loadingNumber"
    class="song-list-table"
  >
    <div>
      <div>
        <span
          v-for="i of loadingNumber"
          :key="i"
          class="song-item block"
          :class="classes"
        >
          <div class="flex items-center gap-2">
            <div
              v-if="showRank"
              class="col-rank"
            >
              <div class="rank-position">
                <span class="placeholder" />
              </div>
            </div>
            <div
              class="col-play"
              :class="{ large: largeAlbumArtwork }"
            >
              <span class="placeholder" />
            </div>
            <div class="flex-1 items-center justify-between xl:flex">
              <div class="flex-1 max-md:mb-1">
                <div class="title">
                  <span class="placeholder" />
                </div>
                <div class="authors">
                  <span class="placeholder" />
                </div>
              </div>

              <div class="flex items-center">
                <div class="product-icon">
                  <span class="product-target">
                    <span class="placeholder" />
                  </span>
                </div>
                <div class="product-icon">
                  <span class="product-target">
                    <span class="placeholder" />
                  </span>
                </div>
                <div class="product-icon">
                  <span class="product-target">
                    <span class="placeholder" />
                  </span>
                </div>
                <div class="product-icon">
                  <span class="product-target">
                    <span class="placeholder" />
                  </span>
                </div>
                <div class="product-icon">
                  <span class="product-target">
                    <span class="placeholder" />
                  </span>
                </div>
              </div>
            </div>
            <div class="product-icon">
              <span class="placeholder" />
            </div>
          </div>
        </span>
      </div>
    </div>
  </div>

  <div
    v-else
    class="song-list-table"
  >
    <div>
      <!-- <div
        v-if="showChartData && showWeeklyTrends"
        class="table-header"
      >
        <div class="flex w-full items-center gap-2">
          <div class="flex-1"></div>
          <div class="weeks-on-chart-label">Weeks on chart</div>
        </div>
      </div> -->
      <div
        ref="wrapper"
        class="wrapper"
        :class="{ reorder: reorder, dragging: dragging }"
      >
        <component
          :is="componentIs"
          v-for="song of songs"
          :key="song.songNumber"
          class="song-item block"
          :data-menu-item="dataTitle ? dataTitle + ' - ' + song.title : ''"
          :class="classes"
          :to="
            (!dontGoToSong && `/songs/${song.songNumber}/${song.slug}`) ||
            undefined
          "
          :data-songNumber="song.songNumber"
          :data-slug="song.slug"
          @click="
            (e: Event) =>
              !reorder && onClick(e, song.songNumber, song.slug, song.title)
          "
        >
          <div class="flex items-start gap-2 lg:items-center">
            <div
              v-if="showRank && !reorder"
              class="col-rank"
            >
              <div class="rank-position">{{ song.rank }}</div>
              <div
                v-if="song.rankMovement && showWeeklyTrends"
                class="rank-movement"
                :class="{
                  up: song.rankMovement > 0,
                  down: song.rankMovement < 0,
                }"
              >
                <FAIcon
                  v-if="song.rankMovement > 0"
                  class="fa-solid fa-caret-up"
                />
                <FAIcon
                  v-if="song.rankMovement < 0"
                  class="fa-solid fa-caret-down"
                />
                {{ Math.abs(song.rankMovement) }}
              </div>
            </div>
            <div
              v-if="reorder"
              class="mr-2"
            >
              <FAIcon class="fa-light fa-grip-dots px-1 pr-3 text-secondary" />
            </div>
            <div
              class="col-play mr-2"
              :class="{ large: largeAlbumArtwork }"
            >
              <SongRowPreview
                v-if="song.soundSample?.audioUrl && !reorder"
                :title="song.title"
                :preview-url="song.soundSample.audioUrl"
                :album-cover-url="song.soundSample.imageUrl"
                class="preview"
                :always-white="showAlbumArtwork"
                :with-album-cover="showAlbumArtwork || song.enableSongVersion"
              />
              <Image
                v-else-if="song.soundSample.imageUrl"
                :src="song.soundSample.imageUrl"
                :alt="song.title"
                class="album-artwork"
                :restrict-width="80"
              />
              <div
                v-else-if="showAlbumArtwork"
                class="album-artwork-placeholder"
              >
                <FAIcon class="fa-solid fa-music" />
              </div>
            </div>
            <div class="flex-1 items-center justify-between gap-4 xl:flex">
              <div class="flex-1 items-center justify-between gap-2 xl:flex">
                <div class="flex-1 max-md:mb-1">
                  <div
                    class="title"
                    :class="{
                      'asian-text': hasAsianText(song.title),
                    }"
                  >
                    {{ song.title }}
                    <span
                      v-if="song.enableSongVersion"
                      class="based-on-recording-badge"
                      :title="$t('SV.LIT_BasedOnRecording')"
                    >
                      <i
                        class="based-on-recording-icon fa-light fa-microphone-stand text-primary"
                      />
                    </span>
                  </div>
                  <div
                    v-if="song.enableSongVersion"
                    class="authors"
                    :class="{
                      'asian-text': hasAsianText(
                        (song.album || '') +
                          (song.artist || '') +
                          song.authors.join(','),
                      ),
                    }"
                  >
                    {{ song.album }} / {{ song.artist }} /
                    {{ song.authors.join(", ") }}
                  </div>
                  <div
                    v-else
                    class="authors"
                    :class="{
                      'asian-text': hasAsianText(song.authors.join(',')),
                    }"
                  >
                    {{ song.authors.join(", ") }}
                  </div>
                </div>
                <ChartBadges
                  v-if="showChartData && showWeeklyTrends && showBadges"
                  :badges="song.badges"
                  class="chart-badges"
                />
              </div>

              <!-- <div
                v-if="song.weeksOnChart && showChartData && showWeeklyTrends"
                class="weeks-on-chart"
              >
                {{ song.weeksOnChart }}
              </div> -->
              <div
                v-if="!reorder"
                class="md:w-100 grid w-52 grid-cols-5"
              >
                <div class="product-icon">
                  <router-link
                    v-if="song.productExists.lyrics && !hideExtraIcons"
                    :to="`/songs/${song.songNumber}/${song.slug}/viewlyrics`"
                    class="product-target"
                  >
                    <FAIcon
                      class="fa-kit fa-ccli-lyrics"
                      :title="$t('SV.LIT_HasLyrics')"
                    />
                  </router-link>
                  <FAIcon
                    v-else-if="song.productExists.lyrics && hideExtraIcons"
                    class="fa-kit fa-ccli-lyrics"
                    :title="$t('SV.LIT_HasLyrics')"
                  />
                </div>
                <div class="product-icon">
                  <router-link
                    v-if="song.productExists.chords && !hideExtraIcons"
                    :to="`/songs/${song.songNumber}/${song.slug}/viewchordsheet`"
                    class="product-target"
                  >
                    <FAIcon
                      class="fa-kit fa-ccli-chord"
                      :title="$t('SV.LIT_HasChords')"
                    />
                  </router-link>
                  <FAIcon
                    v-else-if="song.productExists.chords && hideExtraIcons"
                    class="fa-kit fa-ccli-chord"
                    :title="$t('SV.LIT_HasChords')"
                  />
                </div>
                <div class="product-icon">
                  <router-link
                    v-if="song.productExists.lead && !hideExtraIcons"
                    :to="`/songs/${song.songNumber}/${song.slug}/viewleadsheet`"
                    class="product-target"
                  >
                    <FAIcon
                      class="fa-kit fa-ccli-lead"
                      :title="$t('SV.LIT_HasLeadSheet')"
                    />
                  </router-link>
                  <FAIcon
                    v-else-if="song.productExists.lead && hideExtraIcons"
                    class="fa-kit fa-ccli-lead"
                    :title="$t('SV.LIT_HasLeadSheet')"
                  />
                </div>
                <div class="product-icon">
                  <router-link
                    v-if="song.productExists.vocal && !hideExtraIcons"
                    :to="`/songs/${song.songNumber}/${song.slug}/viewvocalsheet`"
                    class="product-target"
                  >
                    <FAIcon
                      class="fa-kit fa-ccli-vocal"
                      :title="$t('SV.LIT_HasVocalSheet')"
                    />
                  </router-link>
                  <FAIcon
                    v-else-if="song.productExists.vocal && hideExtraIcons"
                    class="fa-kit fa-ccli-vocal"
                    :title="$t('SV.LIT_HasVocalSheet')"
                  />
                </div>
                <div class="product-icon">
                  <a
                    v-if="getMultitracksUrl(song.songNumber) && !hideExtraIcons"
                    :href="getMultitracksUrl(song.songNumber) || ''"
                    class="product-target"
                    target="_blank"
                    data-loopcommunity-link="Search Result"
                    :data-songnumber="song.songNumber"
                    @click.stop.prevent="goToMultitracksLink(song.songNumber)"
                  >
                    <FAIcon
                      class="fa-kit fa-loop-logo text-loop-green"
                      :title="$t('SV.LIT_HasMultitracks')"
                    />
                  </a>
                  <FAIcon
                    v-else-if="
                      getMultitracksUrl(song.songNumber) && hideExtraIcons
                    "
                    class="fa-kit fa-loop-logo text-loop-green"
                    :title="$t('SV.LIT_HasMultitracks')"
                  />
                </div>
              </div>
            </div>

            <div
              v-if="!reorder && !hideExtraIcons"
              class="product-icon"
            >
              <AddToFavorites
                class="mr-0"
                :title="song.title"
                :song-number="song.songNumber"
                @click.stop.prevent
              />
            </div>
          </div>
        </component>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.song-list-table {
  @apply w-full;
}
:deep() {
  .product-target,
  button.favorite {
    @apply px-2 py-1;
    &:hover {
      @apply rounded bg-light-100;
    }
  }
}

.product-target .placeholder {
  @apply mx-1;
}

.table {
  .col-play,
  .product-icon {
    @apply w-0;
  }
}

.product-icon {
  @apply text-lg text-primary;
}
.title {
  @apply mb-1 line-clamp-1 text-left text-lg leading-5;
  &:not(.asian-text) {
    padding-bottom: 0.14em;
  }
}
.table {
  @apply w-full;
}
.authors {
  @apply line-clamp-1 text-left text-sm leading-4 text-gray-600;
  &:not(.asian-text) {
    padding-bottom: 0.14em;
  }
}
.wrapper:not(.reorder) .song-item {
  @apply transition-all;
}

.song-item {
  @apply w-full bg-base-100 px-5 py-4;
  :deep() {
    .favorite:not(.favorite-active) {
      @apply text-light-900;
    }
  }
}

.song-item-add-to-list {
  @apply px-0 md:px-5;
}
.wrapper:not(.dragging) .song-item {
  &:hover,
  &:focus-visible {
    @apply bg-black/5;

    :deep() {
      .favorite:not(.favorite-active) {
        @apply text-gray-100;
      }
      .authors {
        @apply text-gray-700;
      }
    }
  }
}

.col-rank {
  @apply w-[50px] shrink-0 text-center xl:w-[100px];
}
.rank-position {
  @apply font-display text-2xl font-extrabold xl:text-6xl;
}
.rank-movement {
  @apply font-display font-bold xl:text-lg;
  &.up {
    @apply text-ccli-green;
  }
  &.down {
    @apply text-ccli-red;
  }
}

.col-rank .placeholder {
  width: 75px;
}

.col-play .placeholder {
  @apply block h-12 w-12 rounded-lg text-2xl;
}
.col-play.large .placeholder {
  @apply h-16 w-16 lg:h-20 lg:w-20;
}
.title .placeholder {
  width: 200px;
}
.authors .placeholder {
  width: 300px;
}
.product-icon .placeholder {
  @apply text-xl;
  width: 1em;
}
.col-play {
  @apply relative flex h-12 w-12 items-center justify-center max-md:hidden;
  .album-artwork-placeholder {
    @apply flex h-full w-full items-center justify-center rounded-lg bg-base-300;
  }
  &.large {
    @apply h-16 w-16 lg:h-20 lg:w-20;
    &:deep(button.with-album-cover) {
      @apply h-full w-full;
    }
    &:deep(.album-artwork-wrapper) {
      @apply h-full w-full;
    }
  }
}

.wrapper:deep() {
  .sortable-drag {
    opacity: 0;
  }
  .sortable-chosen {
    @apply bg-primary text-white;
    .authors {
      @apply text-white;
    }
    i,
    svg {
      @apply text-white;
    }
  }
}

.weeks-on-chart {
  @apply w-32 shrink-0 text-center text-lg font-bold max-xl:hidden;
}
.table-header {
  @apply px-5 max-xl:hidden;
}
.weeks-on-chart-label {
  @apply mr-[246px] w-32 text-center;
}
.chart-badges {
  @apply my-1;
}
.based-on-recording-badge {
  @apply ml-1 inline-flex aspect-square items-center justify-center rounded bg-light-300 px-2 py-1;
  img {
    @apply h-4 w-auto;
  }
}
.dark .based-on-recording-badge {
  @apply bg-gray-900;
}
/* .chart-badges-desktop.song-badges {
    @apply max-lg:hidden;
  } */
</style>
